<template>
  <div>
    <o-table
      class="ma-2"
      :data="tableData"
      v-if="props.suppliers && props.suppliers.length > 0"
      hoverable
      :loading="isLoading"
      :paginated="true"
      :per-page="100"
    >
      <o-table-column v-for="column in columns" v-bind="column">
        <template v-if="column.searchable" #searchable="props">
          <o-input
            v-model="props.filters[props.column.field]"
            placeholder="Search..."
            size="small"
          />
        </template>
        <template #default="props">
          <p>{{ props.row[column.field] }}</p>
        </template>
      </o-table-column>
      <o-table-column label="Delete" v-slot="props">
        <AppkitIcon
          style="cursor: pointer"
          icon="icon-delete-outline"
          @click="onDelete(props.row)"
        />
      </o-table-column>
    </o-table>

    <SupplierDialog
      :can-upload-business-partner="
        authenticationStore.canUploadBusinessPartners()
      "
      :open-dialog="isDialogOpen"
      :supplier="_supplier"
      :edit-mode="true"
      @close-dialog="close"
      @change="onDialogChange"
    />
  </div>
</template>

<script setup lang="ts">
import AppkitIcon from "@/components/custom/AppkitIcon.vue";
import { Supplier } from "@/models/supplier";
import { useSnackBarStore } from "@/stores/snackBarStore";
import { ref, computed } from "vue";
import SupplierDialog from "@/views/supplier-registry/SupplierDialog.vue";
import { useAuthenticationStore } from "@/stores/authentication";
import { useSuppliersStore } from "@/stores/suppliersStore";

const authenticationStore = useAuthenticationStore();
const suppliersStore = useSuppliersStore();

interface DisplaySupplier extends Supplier {
  businessRelationshipText: string;
}

const props = defineProps<{
  suppliers: Supplier[];
  isLoading: boolean;
}>();

const emits = defineEmits<{
  (e: "change"): void;
}>();

const snackBarStore = useSnackBarStore();

const isDialogOpen = ref(false);
const _supplier = ref(new Supplier());

const close = () => (isDialogOpen.value = false);
const emitChange = () => emits("change");

function onDialogChange(): void {
  _supplier.value = new Supplier();
  close();
  emitChange();
}

async function onDelete(displaySupplier: DisplaySupplier): Promise<void> {
  const supplier: Supplier = {
    companyName: displaySupplier.companyName,
    isBusinessPartner: displaySupplier.isBusinessPartner,
    email: displaySupplier.email,
  };

  try {
    await suppliersStore.removeSupplierFromList(supplier);
    emitChange();
    snackBarStore.showSuccess();
  } catch (error: any) {
    snackBarStore.showError(error.message);
  }
}

function getBusinessRelationshipText(isBusinessPartner: boolean): string {
  switch (isBusinessPartner) {
    case true:
      return "Business partner";
    case false:
      return "Supplier";
  }
}

const tableData = computed(() =>
  props.suppliers.map((s) => ({
    companyName: s.companyName,
    email: s.email,
    businessRelationshipText: getBusinessRelationshipText(s.isBusinessPartner),
  }))
);

const columns = ref([
  {
    field: "companyName",
    label: "Company",
    sortable: true,
    searchable: true,
  },
  {
    field: "email",
    label: "Email",
    sortable: true,
    searchable: true,
  },
  {
    field: "businessRelationshipText",
    label: "Business Relationship",
    sortable: true,
    searchable: true,
  },
]);
</script>
